import { useEffect, useRef, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import { fetchRepositories } from '../services/RepositoryService';
import { fetchRepositoriesSuccess } from '../store/slices/repositorySlice';
import { Repository } from '../types/Repository.types';

interface UseRepositoryPollingOptions {
  systemId: string | undefined;
  pollingInterval?: number;
  onSyncComplete?: (repository: Repository) => void;
}

/**
 * Smart polling hook that only polls when repositories have syncInProgress=true
 *
 * Features:
 * - Auto-starts polling when any repository is syncing
 * - Auto-stops when all syncs complete
 * - Notifies via callback when sync completes
 * - Prevents multiple intervals from running
 */
export function useRepositoryPolling({
  systemId,
  pollingInterval = 3000, // 3 seconds
  onSyncComplete
}: UseRepositoryPollingOptions) {
  const dispatch = useAppDispatch();
  const repositories = useAppSelector((state) => state.repository.repositories);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousSyncingRef = useRef<Set<string>>(new Set());
  const pollCountRef = useRef<number>(0);

  // Check if any repository is syncing
  const hasSyncingRepositories = repositories.some(
    (repo) => repo.syncInProgress
  );

  // Debug log
  useEffect(() => {
    const syncingRepos = repositories.filter((r) => r.syncInProgress);
    if (syncingRepos.length > 0) {
      console.log(
        '🔄 Syncing repositories detected:',
        syncingRepos.map((r) => ({ id: r.id, name: r.name }))
      );
    }
  }, [repositories]);

  const pollRepositories = useCallback(async () => {
    if (!systemId) return;

    pollCountRef.current += 1;
    console.log(
      `📡 Poll #${pollCountRef.current} at ${new Date().toLocaleTimeString()}`
    );

    try {
      const response = await fetchRepositories({ aiSystemId: systemId });
      dispatch(fetchRepositoriesSuccess(response));

      // Check for newly completed syncs
      if (onSyncComplete) {
        const currentSyncing = new Set(
          response.data
            .filter((repo) => repo.syncInProgress)
            .map((repo) => repo.id)
        );

        // Find repositories that were syncing but are not anymore
        previousSyncingRef.current.forEach((repoId) => {
          if (!currentSyncing.has(repoId)) {
            const completedRepo = response.data.find(
              (repo) => repo.id === repoId
            );
            if (completedRepo) {
              onSyncComplete(completedRepo);
            }
          }
        });

        previousSyncingRef.current = currentSyncing as Set<string>;
      }
    } catch (error) {
      console.error('Polling failed:', error);
      // Don't stop polling on error, just log it
    }
  }, [systemId, dispatch, onSyncComplete]);

  useEffect(() => {
    // Start polling when repositories are syncing
    if (hasSyncingRepositories && !intervalRef.current) {
      console.log(
        `🔄 Starting repository polling (interval: ${pollingInterval}ms)`
      );
      pollCountRef.current = 0;

      // Poll immediately (don't wait for first interval)
      pollRepositories();

      // Then poll at interval
      const interval = setInterval(() => {
        pollRepositories();
      }, pollingInterval);

      intervalRef.current = interval;
    }

    // Stop polling when no repositories are syncing
    if (!hasSyncingRepositories && intervalRef.current) {
      console.log(
        `✅ Stopping repository polling (completed ${pollCountRef.current} polls)`
      );
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      previousSyncingRef.current.clear();
      pollCountRef.current = 0;
    }
  }, [hasSyncingRepositories, pollingInterval]); // Remove pollRepositories from dependencies!

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        console.log('🧹 Cleaning up polling interval on unmount');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  return {
    isPolling: !!intervalRef.current,
    hasSyncingRepositories
  };
}
